import { Component, Inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';
import {
  normalizeResourceSubType,
  normalizeResourceType,
  RESOURCE_SUB_TYPE_OPTIONS,
  RESOURCE_TYPE_OPTIONS
} from '../../core/config/resource-enums.config';

export interface EditResourceDialogData {
  resource: {
    id: string | number;
    name: string;
    registration: string;
    category: string;
    type: string;
    subType: string;
    nextServiceDate: string | null;
    issuanceExpiryDate: string | null;
  };
  categoryOptions: Array<{ value: string; label: string }>;
  typeOptions?: Array<{ value: string; label: string }>;
  subTypeOptions?: Array<{ value: string; label: string }>;
}

@Component({
  selector: 'app-edit-resource-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe
    ,EnterSubmitDirective
  ],
  template: `
    <h2 mat-dialog-title>{{ 'common.edit' | t }} {{ entityLabel() }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="submit()">
    <mat-dialog-content>
      <div class="edit-form">
        <mat-form-field>
          <mat-label>{{ fieldLabel('name') }}</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ fieldLabel('registration') }}</mat-label>
          <input matInput formControlName="registration" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ fieldLabel('subType') }}</mat-label>
          <mat-select formControlName="subType">
            @for (option of subTypeOptions(); track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ fieldLabel('type') }}</mat-label>
          <mat-select formControlName="type">
            @for (option of typeOptions(); track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ fieldLabel('category') }}</mat-label>
          <mat-select formControlName="category">
            @for (option of data.categoryOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ fieldLabel('nextServiceDate') }}</mat-label>
          <input matInput type="date" formControlName="nextServiceDate" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ fieldLabel('issuanceExpiryDate') }}</mat-label>
          <input matInput type="date" formControlName="issuanceExpiryDate" />
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button type="submit" color="primary" [disabled]="form.invalid">{{ 'common.apply' | t }}</button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    :host {
      display: flex;
      flex-direction: column;
      max-height: 100%;
      min-height: 0;
    }
    mat-dialog-content {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
      overflow: auto;
    }
    mat-dialog-actions {
      margin-top: auto;
      border-top: 1px solid #d7e1f5;
      background: #fff;
      padding-top: 12px;
    }
    .edit-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      min-width: 860px;
      margin-top: 8px;
    }
    .edit-form mat-form-field {
      width: 100%;
    }
    @media (max-width: 1024px) and (orientation: portrait), (max-width: 767px) {
      .edit-form {
        grid-template-columns: 1fr;
        min-width: 280px;
      }
    }
  `
})
export class EditResourceDialogComponent {
  readonly form;

  constructor(
    private readonly fb: FormBuilder,
    private readonly localization: LocalizationService,
    readonly dialogRef: MatDialogRef<EditResourceDialogComponent, Record<string, unknown> | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: EditResourceDialogData
  ) {
    this.form = this.fb.nonNullable.group({
      name: [data.resource.name, [Validators.required]],
      registration: [data.resource.registration, [Validators.required]],
      category: [data.resource.category, [Validators.required]],
      type: [normalizeResourceType(data.resource.type) || data.resource.type, [Validators.required]],
      subType: [normalizeResourceSubType(data.resource.subType) || data.resource.subType, [Validators.required]],
      nextServiceDate: [this.toDateInputValue(data.resource.nextServiceDate)],
      issuanceExpiryDate: [this.toDateInputValue(data.resource.issuanceExpiryDate)]
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.dialogRef.close({
      ...raw,
      nextServiceDate: raw.nextServiceDate || null,
      issuanceExpiryDate: raw.issuanceExpiryDate || null
    });
  }

  entityLabel(): string {
    const key = 'entityAdd.resources';
    const translated = this.localization.translate(key);
    return translated === key ? 'Resource' : translated;
  }

  fieldLabel(fieldKey: string): string {
    const map: Record<string, string> = {
      name: 'field.resourceName',
      category: 'field.categories'
    };
    const key = map[fieldKey] ?? `field.${fieldKey}`;
    const translated = this.localization.translate(key);
    return translated === key ? fieldKey : translated;
  }

  typeOptions(): Array<{ value: string; label: string }> {
    return this.data.typeOptions ?? RESOURCE_TYPE_OPTIONS.map((item) => ({ value: item.value, label: this.localization.translate(item.labelKey) }));
  }

  subTypeOptions(): Array<{ value: string; label: string }> {
    return this.data.subTypeOptions ?? RESOURCE_SUB_TYPE_OPTIONS.map((item) => ({ value: item.value, label: this.localization.translate(item.labelKey) }));
  }

  private toDateInputValue(value: unknown): string {
    if (Array.isArray(value) && value.length >= 3) {
      return this.toIsoDateParts(value[0], value[1], value[2]);
    }

    if (value && typeof value === 'object') {
      const row = value as Record<string, unknown>;
      const nested = row['date'] ?? row['value'] ?? row['iso'] ?? row['isoDate'] ?? row['localDate'];
      if (nested != null && nested !== value) {
        return this.toDateInputValue(nested);
      }
      const year = row['year'] ?? row['yyyy'];
      const month = row['month'] ?? row['monthValue'] ?? row['mm'];
      const day = row['day'] ?? row['dayOfMonth'] ?? row['dd'];
      const fromParts = this.toIsoDateParts(year, month, day);
      if (fromParts) {
        return fromParts;
      }
    }

    const raw = `${value ?? ''}`.trim();
    if (!raw) {
      return '';
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    const localMatch = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
    if (localMatch) {
      const [, day, month, year] = localMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
    }

    return '';
  }

  private toIsoDateParts(yearValue: unknown, monthValue: unknown, dayValue: unknown): string {
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      return '';
    }
    if (year < 1000 || month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
}
